import React, { useState } from "react";
import { useUser } from "../../context/userContext";
import axios from "axios";
import { ReactComponent as CloseIcon } from "../../assets/icons/chevron_icon.svg";

const TopUpDialog = ({ onClose, setIsPaymentSuccessDialogOpen }) => {
  const { userData, fetchDashboardData } = useUser();
  const { subscription_plan, subscription_frequency, email, token } =
    userData;

  const [selectedPrice, setSelectedPrice] = useState(10);
  const dailyPlans = [10, 20, 30, 50];
  const monthlyPlans = [50, 100, 150, 200];
  const yearlyPlans = [500, 1500, 3000, 6000];

  // Object to store token values based on price
  const dailyPlansTokenValues = {
    10: 15,
    20: 35,
    30: 55,
    50: 75,
  };

  const monthlyPlansTokenValues = {
    50: 80,
    100: 160,
    150: 240,
    200: 320,
  };

  const yearlyPlansTokenValues = {
    500: 800,
    1500: 2400,
    3000: 4800,
    6000: 9000,
  };

  const [activeTab, setActiveTab] = useState(2);
  const handleTabClick = (tabNum) => {
    setActiveTab(tabNum);
    if (tabNum === 1) {
      if (subscription_frequency === "Yearly") {
        setSelectedPrice(500);
      } else {
        setSelectedPrice(50);
      }
    } else {
      setSelectedPrice(10);
    }
  };

  // Function to apply styles
  const isButtonActive = (price) => {
    return selectedPrice === price;
  };

  const handleButtonClick = (price) => {
    setSelectedPrice(price);
  };

  const handleCheckoutClick = async (flag) => {
    const API_URL = "https://soliboat.solidaridadasia.com/";

    try {
      onClose(false);

      let subscriptionPlan;
      switch (subscription_plan) {
        case "Student":
          subscriptionPlan = "student";
          break;
        case "Professional":
          subscriptionPlan = "pro";
          break;
        case "Business":
          subscriptionPlan = "business";
          break;
        case "Enterprise":
          subscriptionPlan = "enterprise";
          break;
        default:
          subscriptionPlan = "free_tier";
          break;
      }

      // Step 0: Call the Subscription API
      const subscriptionResponse = await axios.post(
        `${API_URL}subscription`,
        {
          email: email,
          subscription_type: subscriptionPlan,
          daily_subscription: flag,
          add_on: !flag
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      console.log("Subscription Response:", subscriptionResponse.data);

      if (subscriptionResponse.data.success) {
        // Step 1: Create an order on server
        const orderResponse = await axios.post(
          `${API_URL}rp_create_orderid`,
          {
            base_amount: selectedPrice,
            tax_inclusive_amount: selectedPrice,
            email: email,
            subscription_frequency: subscription_frequency,
            subscription_type: subscription_plan,
            daily_subscription: flag,
            add_on: !flag
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        const orderData = orderResponse.data.data[0].order_details;
        console.log("Order Data:", orderData);

        // Step 2: Initialize Razorpay
        const options = {
          key: process.env.RAZOR_PAY_KEY_ID,
          name: "XplorEase",
          description: "Test Daily Top-Up Transaction",
          order_id: orderData.id,
          handler: async (response) => {
            try {
              console.log(response);
              // Step 3: Capture the payment on server
              const paymentId = response.razorpay_payment_id;
              const orderID = response.razorpay_order_id;
              const captureResponse = await axios.post(
                `${API_URL}rp_payment_capture`,
                {
                  email: email,
                  payment_id: paymentId,
                  amount: orderData.amount,
                  order_id: orderID,
                  subscription_frequency: subscription_frequency,
                  subscription_type: subscription_plan,
                  daily_subscription: flag,
                  add_on: !flag
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  withCredentials: true,
                }
              );
              console.log("Capture Response:", captureResponse.data);
              setIsPaymentSuccessDialogOpen(true);
              fetchDashboardData();
            } catch (captureError) {
              console.error("Error capturing payment:", captureError);
            }
          },
          theme: {
            color: "#3559E0",
          },
        };

        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      } else {
        console.error(
          "Subscription API failed:",
          subscriptionResponse.data.message
        );
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.24)] z-50">
      <div className="fixed inset-0 bg-black opacity-50" />

      <div className="relative flex flex-col justify-center items-center p-2 w-[90%] md:w-3/5 lg:w-2/5 xl:w-96 bg-[#f0f4f7] rounded-xl">
        <div
          className="absolute top-4 left-3 flex items-center cursor-pointer"
          onClick={() => onClose(false)}
        >
          <CloseIcon className="w-6 h-6 -rotate-90" />
          <p className="ml-1">Back</p>
        </div>

        <h3 className="mt-8 mb-2 text-2xl font-semibold">Top-Up</h3>

        <h3 className="mt-1 mb-4 text-lg font-medium">How many tokens?</h3>

        <div className="flex justify-center items-center w-full mb-4">
          <div className="flex flex-col justify-center items-center w-1/2 text-rose-400">
            <p className="text-4xl font-extrabold">
              {activeTab === 1
                ? subscription_frequency === "Yearly"
                  ? yearlyPlansTokenValues[selectedPrice]
                  : monthlyPlansTokenValues[selectedPrice]
                : dailyPlansTokenValues[selectedPrice]}
            </p>
            <p className="font-bold">Tokens</p>
          </div>
          <div className="h-20 border border-neutral-200" />
          <div className="flex flex-col justify-center items-center w-1/2 text-blue-500">
            <p
              className={`${
                activeTab === 1
                  ? "text-xl md:text-2xl mb-1 font-bold"
                  : "text-3xl font-extrabold"
              }`}
            >
              {activeTab === 1 ? "Existing Plan" : "1 Day"}
            </p>
            <p className="font-bold">Validity</p>
          </div>
        </div>

        {/* Plan Tabs */}
        <div className="flex w-full px-4 mb-4">
          <div className="flex justify-center items-center w-full">
            <button
              className={`flex justify-center items-center w-4/5 px-2 py-2 font-medium rounded-l ${
                activeTab === 1
                  ? "text-neutral-700 bg-white"
                  : "text-neutral-700 bg-slate-200"
              } ${
                subscription_frequency === "One-Time" ||
                subscription_frequency === "NA"
                  ? "cursor-not-allowed"
                  : ""
              }`}
              onClick={() => handleTabClick(1)}
              disabled={
                subscription_frequency === "One-Time" ||
                subscription_frequency === "NA"
              }
              title={
                (subscription_frequency === "One-Time" ||
                  subscription_frequency === "NA") &&
                "You are not subscribed to any plan."
              }
            >
              Add-Ons
            </button>
            <button
              className={`flex justify-center items-center w-4/5 px-2 py-2 font-medium rounded-r ${
                activeTab === 2
                  ? "text-neutral-700 bg-white"
                  : "text-neutral-700 bg-slate-200"
              }`}
              onClick={() => handleTabClick(2)}
            >
              Daily Plans
            </button>
          </div>
        </div>

        <div className="w-[90%] mb-4 border-b border-neutral-200" />

        {/* Plan Buttons */}
        <div className="w-full px-4 mb-4 grid grid-cols-2 gap-4">
          {(activeTab === 1
            ? subscription_frequency === "Yearly"
              ? yearlyPlans
              : monthlyPlans
            : dailyPlans
          ).map((price, index) => (
            <button
              key={index}
              className={`h-20 md:h-24 bg-white text-neutral-700 p-2 rounded-2xl shadow-one ${
                isButtonActive(price) ? "border-4 border-blue-300" : ""
              }`}
              onClick={() => handleButtonClick(price)}
            >
              <p className="text-2xl md:text-3xl font-extrabold text-neutral-700">
                ₹ {price}
              </p>
            </button>
          ))}
        </div>

        <div className="w-[90%] mb-4 border-b border-neutral-200" />

        {/* Pay Button */}
        <div className="flex justify-center items-center w-[90%] mb-4 bg-primary/10 rounded">
          <button
            className="flex justify-center items-center w-full bg-blue-500 text-white m-4 px-2 py-2 rounded"
            onClick={() => handleCheckoutClick(activeTab === 1 ? false : true )}
          >
            <p>Pay - ₹ {selectedPrice}</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpDialog;
